import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { Checkbox } from 'components/ui/Checkbox'
import {
  Brain,
  CheckCircle2,
  Zap,
  Heart,
  Star,
  Sparkles,
  X,
  Plus,
  HelpCircle,
  Lock,
  Unlock
} from 'lucide-react'

export interface RecommendedSkillsData {
  hard_skills_mentionnées?: string[]
  hard_skills_non_mentionnées?: string[]
  soft_skills_mentionnées?: string[]
  soft_skills_non_mentionnées?: string[]
  nice_to_have_mentionnées?: string[]
  nice_to_have_non_mentionnées?: string[]
}

interface RecommendedSkillsModalProps {
  isOpen: boolean
  onClose: () => void
  onAccept: (
    selected: {
      hard_skills_non_mentionnées: string[]
      soft_skills_non_mentionnées: string[]
      nice_to_have_non_mentionnées: string[]
    },
    weights?: {
      hard_skills: number
      soft_skills: number
      nice_to_have: number
    },
    skillWeights?: Record<string, number>,
    lockedSkills?: string[]
  ) => void
  skills: RecommendedSkillsData
  initialLockedSkills?: Set<string>
  initialSkillWeights?: {
    hard_skills?: Record<string, number>
    soft_skills?: Record<string, number>
    nice_to_have?: Record<string, number>
  }
  isLoading?: boolean
  initialWeights?: {
    hard_skills: number
    soft_skills: number
    nice_to_have: number
  }
  overlayClassName?: string
}

/* ─── Extracted SkillSection (stable component reference) ─── */
interface SkillSectionProps {
  title: string
  icon: React.ElementType
  mentioned: string[]
  recommended: string[]
  selectedList: string[]
  accentClass: string
  onToggle: (skill: string) => void
  t: any
  weight: number
  onWeightChange: (weight: number) => void
  skillWeights: Record<string, number>
  onSkillWeightClick: (e: React.MouseEvent, skill: string) => void
  lockedSkills: Set<string>
  onSkillLockToggle: (e: React.MouseEvent, skill: string) => void
  skillPrefix: string
}

function SkillSection({
  title,
  icon: Icon,
  mentioned,
  recommended,
  selectedList,
  accentClass,
  onToggle,
  t,
  weight,
  onWeightChange,
  skillWeights,
  onSkillWeightClick,
  lockedSkills,
  onSkillLockToggle,
  skillPrefix,
}: SkillSectionProps) {
  const isSectionEmpty = mentioned.length === 0 && selectedList.length === 0;

  useEffect(() => {
    if (isSectionEmpty && weight !== 0) {
      onWeightChange(0);
    }
  }, [isSectionEmpty, weight, onWeightChange]);

  if (mentioned.length === 0 && recommended.length === 0) return null

  return (
    <div className={`rounded-xl border p-4 ${accentClass} space-y-4`}>
      {/* Section Header */}
      <div className="flex items-center gap-2 pb-2 border-b border-black/5 dark:border-white/5">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/80 dark:bg-black/30 shadow-sm">
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-sm font-bold">{title}</span>
      </div>

      {/* 1. Recommended skills to select */}
      {recommended.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <Sparkles className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" />
            <span className="text-xs font-semibold uppercase tracking-wider opacity-85">
              {t('recommendedSkills.toAdd', 'Recommandations à ajouter')}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {recommended.map((skill, idx) => {
              const isSelected = selectedList.includes(skill)
              const isLocked = lockedSkills.has(skillPrefix + skill)
              return (
                <button
                  key={skill + idx}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (isLocked) return
                    onToggle(skill)
                  }}
                  className={`inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs font-medium border shadow-sm transition-all duration-200 cursor-pointer ${
                    isLocked ? 'cursor-not-allowed opacity-90 hover:scale-100' : 'hover:scale-[1.02]'
                  } ${
                    isSelected
                      ? 'bg-white text-black border-black/20 dark:bg-black/45 dark:text-white dark:border-white/20'
                      : 'bg-transparent text-black/50 border-black/10 dark:text-white/40 dark:border-white/5 line-through opacity-60'
                  }`}
                >
                  <div className="flex h-3.5 w-3.5 items-center justify-center rounded border border-black/20 dark:border-white/25">
                    {isSelected && <Plus className="h-3 w-3 stroke-[3]" />}
                  </div>
                  {skill}
                  <div className="flex items-center gap-1 ml-1">
                    <span 
                      className={`flex h-5 w-5 items-center justify-center rounded-full transition-colors cursor-pointer ${lockedSkills.has(skillPrefix + skill) ? 'bg-blue-600 text-white' : 'bg-black/10 dark:bg-white/10 text-black/70 dark:text-white/70 hover:bg-black/20 dark:hover:bg-white/20'}`}
                      onClick={(e) => onSkillLockToggle(e, skillPrefix + skill)}
                    >
                      {lockedSkills.has(skillPrefix + skill) ? <Lock className="h-2.5 w-2.5" /> : <Unlock className="h-2.5 w-2.5" />}
                    </span>
                    <span 
                      className={`flex h-5 items-center justify-center rounded-full px-1.5 transition-colors cursor-pointer ${isSelected ? 'bg-black/10 dark:bg-white/10 hover:bg-black/20 dark:hover:bg-white/20' : 'bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10'}`}
                      onClick={(e) => onSkillWeightClick(e, skill)}
                    >
                      <span className="text-[10px] opacity-70">{skillWeights[skill] ?? 1}/5</span>
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* 2. Already mentioned skills */}
      {mentioned.length > 0 && (
        <div className="space-y-2 pt-1">
          <span className="text-xs font-semibold uppercase tracking-wider opacity-60 block">
            {t('recommendedSkills.alreadyInText', 'Déjà détectées dans le texte')}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {mentioned.map((skill, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 rounded-lg bg-black/5 dark:bg-white/5 px-2 py-0.5 text-xs opacity-75"
              >
                {skill}
                <div className="flex items-center gap-1 ml-1">
                  <button
                    type="button"
                    className={`flex h-5 w-5 items-center justify-center rounded-full transition-colors cursor-pointer ${lockedSkills.has(skillPrefix + skill) ? 'bg-blue-600 text-white' : 'bg-black/10 dark:bg-white/10 text-black/70 dark:text-white/70 hover:bg-black/20 dark:hover:bg-white/20'}`}
                    onClick={(e) => onSkillLockToggle(e, skillPrefix + skill)}
                  >
                    {lockedSkills.has(skillPrefix + skill) ? <Lock className="h-2.5 w-2.5" /> : <Unlock className="h-2.5 w-2.5" />}
                  </button>
                  <button
                    type="button"
                    className="flex h-5 items-center justify-center rounded-full px-1.5 transition-colors bg-black/10 dark:bg-white/10 hover:bg-black/20 dark:hover:bg-white/20 cursor-pointer"
                    onClick={(e) => onSkillWeightClick(e, skill)}
                  >
                    <span className="text-[10px] opacity-70">{skillWeights[skill] ?? 1}/5</span>
                  </button>
                </div>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 3. AI Matching Weight */}
      <div className={`pt-3 border-t border-black/5 dark:border-white/5 mt-2 ${isSectionEmpty ? 'opacity-60' : ''}`}>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t('recommendedSkills.aiMatchingWeight', 'Poids AI Matching')}
          </label>
          <input
            type="number"
            min={0}
            max={10}
            value={weight}
            disabled={isSectionEmpty}
            onChange={(e) => {
              const val = Number(e.target.value)
              onWeightChange(val >= 0 && val <= 10 ? val : 0)
            }}
            className={`w-16 h-7 text-right text-xs rounded-md border border-input bg-background px-2 py-1 ${isSectionEmpty ? 'cursor-not-allowed opacity-70' : ''}`}
          />
        </div>
        <input
          type="range"
          min={0}
          max={10}
          value={weight}
          disabled={isSectionEmpty}
          onChange={(e) => onWeightChange(Number(e.target.value))}
          className={`w-full h-1.5 rounded-lg appearance-none bg-black/10 dark:bg-white/10 ${isSectionEmpty ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
        />
      </div>
    </div>
  )
}

/* ─── Main Modal ─── */
export function RecommendedSkillsModal({
  isOpen,
  onClose,
  onAccept,
  skills,
  isLoading = false,
  initialWeights,
  initialLockedSkills,
  initialSkillWeights,
  overlayClassName,
}: RecommendedSkillsModalProps) {
  const { t } = useTranslation()

  // Recommended skills (non mentionnées) that can be selected/deselected
  const recommendedHard = skills.hard_skills_non_mentionnées ?? []
  const recommendedSoft = skills.soft_skills_non_mentionnées ?? []
  const recommendedNice = skills.nice_to_have_non_mentionnées ?? []

  // Mentioned skills (read-only info badges)
  const mentionedHard = skills.hard_skills_mentionnées ?? []
  const mentionedSoft = skills.soft_skills_mentionnées ?? []
  const mentionedNice = skills.nice_to_have_mentionnées ?? []

  // Selection states
  const [selectedHard, setSelectedHard] = useState<string[]>([])
  const [selectedSoft, setSelectedSoft] = useState<string[]>([])
  const [selectedNice, setSelectedNice] = useState<string[]>([])

  // Weight states
  const [hardWeight, setHardWeight] = useState(initialWeights?.hard_skills ?? 2)
  const [softWeight, setSoftWeight] = useState(initialWeights?.soft_skills ?? 2)
  const [niceWeight, setNiceWeight] = useState(initialWeights?.nice_to_have ?? 0)

  // Individual Skill weights & locks
  const [skillWeights, setSkillWeights] = useState<Record<string, number>>({})
  const [lockedSkills, setLockedSkills] = useState<Set<string>>(initialLockedSkills ?? new Set())

  // Re-sync lockedSkills when initialLockedSkills changes
  useEffect(() => {
    if (initialLockedSkills) {
      setLockedSkills(initialLockedSkills)
    }
  }, [initialLockedSkills])

  const handleSkillLockToggle = useCallback((e: React.MouseEvent, skillKey: string) => {
    e.preventDefault()
    e.stopPropagation()
    setLockedSkills(prev => {
      const next = new Set(prev)
      const isLocking = !next.has(skillKey)
      if (isLocking) {
        next.add(skillKey)
        // Automatically select the skill if it is locked
        if (skillKey.startsWith('hardskill_')) {
          const name = skillKey.replace('hardskill_', '')
          setSelectedHard(curr => curr.includes(name) ? curr : [...curr, name])
        } else if (skillKey.startsWith('softskill_')) {
          const name = skillKey.replace('softskill_', '')
          setSelectedSoft(curr => curr.includes(name) ? curr : [...curr, name])
        } else if (skillKey.startsWith('nicetohave_')) {
          const name = skillKey.replace('nicetohave_', '')
          setSelectedNice(curr => curr.includes(name) ? curr : [...curr, name])
        }
      } else {
        next.delete(skillKey)
      }
      return next
    })
  }, [])

  const handleSkillWeightClick = useCallback((e: React.MouseEvent, skill: string) => {
    e.preventDefault()
    e.stopPropagation()
    setSkillWeights(prev => {
      const current = prev[skill] ?? 1
      return { ...prev, [skill]: current >= 5 ? 1 : current + 1 }
    })
  }, [])

  // Track whether we already initialized for the current open + skills combo
  const initializedRef = useRef<string | null>(null)

  // Initialize selection only when modal first opens or skills data actually changes
  useEffect(() => {
    const key = JSON.stringify(skills)
    if (isOpen && initializedRef.current !== key) {
      initializedRef.current = key
      setSelectedHard(recommendedHard)
      setSelectedSoft(recommendedSoft)
      setSelectedNice(recommendedNice)
      setHardWeight(initialWeights?.hard_skills ?? 2)
      setSoftWeight(initialWeights?.soft_skills ?? 2)
      setNiceWeight(initialWeights?.nice_to_have ?? 0)

      // Flatten initialSkillWeights into skillWeights
      const flatWeights: Record<string, number> = {}
      if (initialSkillWeights) {
        if (initialSkillWeights.hard_skills) {
          Object.entries(initialSkillWeights.hard_skills).forEach(([skill, val]) => {
            flatWeights[skill] = Number(val)
          })
        }
        if (initialSkillWeights.soft_skills) {
          Object.entries(initialSkillWeights.soft_skills).forEach(([skill, val]) => {
            flatWeights[skill] = Number(val)
          })
        }
        if (initialSkillWeights.nice_to_have) {
          Object.entries(initialSkillWeights.nice_to_have).forEach(([skill, val]) => {
            flatWeights[skill] = Number(val)
          })
        }
      }
      setSkillWeights(flatWeights)
    }
    if (!isOpen) {
      initializedRef.current = null
    }
  }, [isOpen, skills, recommendedHard, recommendedSoft, recommendedNice, initialWeights, initialSkillWeights])

  const totalRecommended = recommendedHard.length + recommendedSoft.length + recommendedNice.length
  const totalMentioned = mentionedHard.length + mentionedSoft.length + mentionedNice.length

  const handleAccept = () => {
    onAccept(
      {
        hard_skills_non_mentionnées: selectedHard,
        soft_skills_non_mentionnées: selectedSoft,
        nice_to_have_non_mentionnées: selectedNice,
      },
      {
        hard_skills: hardWeight,
        soft_skills: softWeight,
        nice_to_have: niceWeight,
      },
      skillWeights,
      Array.from(lockedSkills)
    )
  }

  // Stable toggle callbacks — never change identity, so SkillSection won't re-render unnecessarily
  const toggleHard = useCallback((skill: string) => {
    setSelectedHard(prev =>
      prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]
    )
  }, [])

  const toggleSoft = useCallback((skill: string) => {
    setSelectedSoft(prev =>
      prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]
    )
  }, [])

  const toggleNice = useCallback((skill: string) => {
    setSelectedNice(prev =>
      prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]
    )
  }, [])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      className="max-w-3xl"
      overlayClassName={overlayClassName}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 shadow-lg">
            <Brain className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
              {t('recommendedSkills.title', 'Analyse & Recommandations IA')}
              <Sparkles className="h-4 w-4 text-violet-500" />
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {t(
                'recommendedSkills.subtitleNew',
                "L'IA a extrait les compétences mentionnées et vous suggère des compétences complémentaires recommandées pour maximiser le matching."
              )}
            </p>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

        {/* Skills sections */}
        {totalRecommended === 0 && totalMentioned === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
            <Brain className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">
              {t('recommendedSkills.noSkills', "Aucune compétence recommandée trouvée.")}
            </p>
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <SkillSection
              title={t('recommendedSkills.hardSkills', 'Compétences techniques (Hard Skills)')}
              icon={Zap}
              mentioned={mentionedHard}
              recommended={recommendedHard}
              selectedList={selectedHard}
              onToggle={toggleHard}
              accentClass="border-blue-200 bg-blue-50/50 dark:border-blue-800/40 dark:bg-blue-950/20 text-blue-800 dark:text-blue-200"
              t={t}
              weight={hardWeight}
              onWeightChange={setHardWeight}
              skillWeights={skillWeights}
              onSkillWeightClick={handleSkillWeightClick}
              lockedSkills={lockedSkills}
              onSkillLockToggle={handleSkillLockToggle}
              skillPrefix="hardskill_"
            />
            <SkillSection
              title={t('recommendedSkills.softSkills', 'Soft Skills')}
              icon={Heart}
              mentioned={mentionedSoft}
              recommended={recommendedSoft}
              selectedList={selectedSoft}
              onToggle={toggleSoft}
              accentClass="border-emerald-200 bg-emerald-50/50 dark:border-emerald-800/40 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-200"
              t={t}
              weight={softWeight}
              onWeightChange={setSoftWeight}
              skillWeights={skillWeights}
              onSkillWeightClick={handleSkillWeightClick}
              lockedSkills={lockedSkills}
              onSkillLockToggle={handleSkillLockToggle}
              skillPrefix="softskill_"
            />
            <SkillSection
              title={t('recommendedSkills.niceToHave', 'Avantages & Nice-to-Have')}
              icon={Star}
              mentioned={mentionedNice}
              recommended={recommendedNice}
              selectedList={selectedNice}
              onToggle={toggleNice}
              accentClass="border-amber-200 bg-amber-50/50 dark:border-amber-800/40 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200"
              t={t}
              weight={niceWeight}
              onWeightChange={setNiceWeight}
              skillWeights={skillWeights}
              onSkillWeightClick={handleSkillWeightClick}
              lockedSkills={lockedSkills}
              onSkillLockToggle={handleSkillLockToggle}
              skillPrefix="nicetohave_"
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="gap-2"
          >
            <X className="h-4 w-4" />
            {t('common.close', 'Fermer')}
          </Button>
          {(selectedHard.length > 0 || selectedSoft.length > 0 || selectedNice.length > 0) && (
            <Button
              onClick={handleAccept}
              loading={isLoading}
              className="gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-md"
            >
              <CheckCircle2 className="h-4 w-4" />
              {t('recommendedSkills.accept', 'Valider les compétences')}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
